'use client';

import { cn } from '../../../../lib/utils';
import { PersonFill, ArrowDropDown } from '@crabs-ai/web-icons';
import { useState, useCallback, useEffect, useRef } from 'react';
import Stepper from '../../controls/stepper/Stepper';
import { InspectorControlsStyles as S } from '../inspector-controls-styles';

export type ValueInputFieldLeadingNodeType = 'text' | 'icon';

// NumberControl 호환 인터페이스
export type ValueInputFieldUpdateHandler = (update: {
  num: number;
  commitToUndoStack: boolean;
}) => void;

function roundToStep(value: number, step: number): number {
  const rounded = Math.round(value / step) * step;
  const decimals = step.toString().split('.')[1]?.length || 0;
  return Number(rounded.toFixed(decimals));
}

export interface ValueInputFieldProps {
  /** 현재 값 */
  value?: number;
  /** 값 변경 핸들러 (일반 형태) */
  onChange?: (value: number | string) => void;
  /** 값 변경 핸들러 (NumberControl 호환 형태) */
  setValue?: ValueInputFieldUpdateHandler;
  /** 레이블 표시 여부 */
  isShowLabel?: boolean;
  /** 레이블 텍스트 */
  label?: string;
  /** 접근성 라벨 */
  accessibilityLabel?: string;
  /** 단위 표시 (예: 'px', '%', 's', 'x', 'º' 등) */
  isUnit?: string;
  /** Leading Node 표시 여부 */
  isShowLeadingNode?: boolean;
  /** Leading Node 타입 */
  leadingNodeType?: ValueInputFieldLeadingNodeType;
  /** Leading Node 값 (text일 때 텍스트, icon일 때 ReactNode) */
  leadingNodeValue?: string | React.ReactNode;
  /** Stepper 표시 여부 */
  showStepper?: boolean;
  /** 드롭다운 아이콘 표시 여부 (Popover 프리셋 등에 활용) */
  showDropdownIcon?: boolean;
  /** 드롭다운 열림 상태 (외부 Popover 제어 시 아이콘 회전/선택 상태 동기화) */
  isDropdownOpen?: boolean;
  /** 드롭다운 아이콘 클릭 핸들러 (외부에서 Popover 등을 제어할 때 사용) */
  onDropdownClick?: () => void;
  /** 비활성화 여부 */
  disabled?: boolean;
  /** 최소값 */
  min?: number | null;
  /** 최대값 */
  max?: number | null;
  /** 최대 길이 */
  maxLength?: number;
  /** 스텝 값 (숫자 입력 시 증가/감소 단위) */
  step?: number | null;
  /** 기본값 */
  defaultValue?: number;
  /** Mixed value 상태 (여러 아이템 선택 시) */
  hasMixedValue?: boolean;
  /** Mixed value일 때 표시할 라벨 */
  mixedLabel?: string;
  /** 드래그로 값 변경 가능 여부 */
  enableDrag?: boolean;
  /** 자연수(0-9)만 입력 가능하도록 제한 (소수점·마이너스 차단). 텍스트 입력·스텝(stepper·ArrowUp/Down) 모두 정수로 제한된다. */
  naturalOnly?: boolean;
  /** 컴포넌트 래퍼에 추가할 클래스명 */
  className?: string;
}

const DEFAULT_MAX_LENGTH = 6;
const DEFAULT_STEP = 1;

export const getValueInputSteppedValue = ({
  value,
  direction,
  step = DEFAULT_STEP,
  min,
  max,
  naturalOnly = false,
}: {
  value: number;
  direction: 'up' | 'down';
  step?: number;
  min?: number | null;
  max?: number | null;
  naturalOnly?: boolean;
}): number => {
  const change = direction === 'up' ? step : -step;
  let newValue = roundToStep(value + change, step);

  if (naturalOnly) {
    newValue = Math.round(newValue);
  }

  if (min !== null && min !== undefined) {
    newValue = Math.max(newValue, min);
  }
  if (max !== null && max !== undefined) {
    newValue = Math.min(newValue, max);
  }

  return newValue;
};

export default function ValueInputField({
  value,
  onChange,
  setValue,
  isShowLabel = false,
  label,
  accessibilityLabel,
  isUnit,
  isShowLeadingNode = false,
  leadingNodeType = 'text',
  leadingNodeValue,
  showStepper = true,
  showDropdownIcon = false,
  isDropdownOpen = false,
  onDropdownClick,
  disabled,
  min,
  max,
  maxLength,
  step,
  defaultValue,
  hasMixedValue = false,
  mixedLabel = 'Mixed',
  enableDrag = true,
  naturalOnly = false,
  className,
  ...rest
}: ValueInputFieldProps &
  Omit<
    React.ComponentProps<'input'>,
    'onChange' | 'min' | 'max' | 'step' | 'type' | 'value'
  >) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [initialValue, setInitialValue] = useState(0);
  const [initialX, setInitialX] = useState(0);
  const [wasMixedOnFocus, setWasMixedOnFocus] = useState(false);
  const [inputValue, setInputValue] = useState<string>(() =>
    value !== undefined ? String(value) : '',
  );

  const unit = isUnit;
  const finalMaxLength = maxLength ?? DEFAULT_MAX_LENGTH;
  const finalStep = step ?? DEFAULT_STEP;
  const finalMin = min;
  const finalMax = max;

  const hasStepper = showStepper;
  const hasDropdown = showDropdownIcon;

  const filterInput = useCallback(
    (input: string): string => {
      if (naturalOnly) {
        return input.replace(/[^0-9]/g, '');
      }

      return input
        .replace(/[^0-9.-]/g, '')
        .replace(/\./g, (match, offset, str) => {
          const before = str.substring(0, offset);
          return before.includes('.') ? '' : match;
        });
    },
    [naturalOnly],
  );

  const getCurrentNumericValue = useCallback((): number => {
    const fallbackValue =
      value !== undefined
        ? Number(value)
        : defaultValue !== undefined
          ? Number(defaultValue)
          : 0;
    const rawValue = focused ? inputValue : String(fallbackValue);
    const valueWithoutUnit = unit
      ? rawValue.replace(new RegExp(`${unit}$`), '')
      : rawValue;
    const filteredValue = filterInput(valueWithoutUnit);
    const parsedValue = Number(filteredValue);

    return Number.isNaN(parsedValue) ? fallbackValue : parsedValue;
  }, [defaultValue, filterInput, focused, inputValue, unit, value]);

  const setDisplayedInputValue = useCallback(
    (newValue: number) => {
      const newValueStr = String(newValue);

      if (focused || !unit || !newValueStr) {
        setInputValue(newValueStr);
        return;
      }

      setInputValue(`${newValueStr}${unit}`);
    },
    [focused, unit],
  );

  const notifyValueChange = useCallback(
    (newValue: number, commitToUndoStack: boolean) => {
      setDisplayedInputValue(newValue);

      if (setValue) {
        setValue({ num: newValue, commitToUndoStack });
      } else if (onChange) {
        onChange(newValue);
      }
    },
    [onChange, setDisplayedInputValue, setValue],
  );

  useEffect(() => {
    if (focused) return;

    if (hasMixedValue && !dragging) {
      setInputValue(mixedLabel);
      return;
    }

    if (value !== undefined) {
      const valueStr = String(value);
      if (unit && valueStr) {
        setInputValue(`${valueStr}${unit}`);
      } else {
        setInputValue(valueStr);
      }
    }
  }, [value, focused, dragging, hasMixedValue, mixedLabel, unit]);

  const handleBeforeInput = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      // @ts-expect-error - beforeinput 이벤트의 data 속성
      const inputData = e.nativeEvent.data;
      if (inputData) {
        const filtered = filterInput(inputData);
        if (filtered !== inputData) {
          e.preventDefault();
          return false;
        }
      }
    },
    [filterInput],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const allowedKeys = [
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
      ];

      const isAllowedKey = allowedKeys.includes(e.key);
      // Keep native text selection, clipboard and undo on both macOS and Windows.
      const isTextShortcut =
        (e.ctrlKey || e.metaKey) &&
        ['a', 'c', 'v', 'x', 'z', 'y'].includes(e.key.toLowerCase());
      if (isTextShortcut && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.select();
        return;
      }

      if (e.key === 'Enter' || e.key === 'Escape') {
        e.preventDefault();
        inputRef.current?.blur();
        return;
      }

      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const newValue = getValueInputSteppedValue({
          value: getCurrentNumericValue(),
          direction: e.key === 'ArrowUp' ? 'up' : 'down',
          step: finalStep,
          min: finalMin,
          max: finalMax,
          naturalOnly,
        });
        notifyValueChange(newValue, true);
        rest.onKeyDown?.(e);
        return;
      }

      const isNumberKey = /^[0-9]$/.test(e.key);
      const isDecimalKey = !naturalOnly && e.key === '.';
      const isMinusKey = !naturalOnly && e.key === '-';

      if (
        !isNumberKey &&
        !isDecimalKey &&
        !isMinusKey &&
        !isAllowedKey &&
        !isTextShortcut
      ) {
        e.preventDefault();
      }

      if (rest.onKeyDown) {
        rest.onKeyDown(e);
      }
    },
    [
      finalMax,
      finalMin,
      finalStep,
      getCurrentNumericValue,
      naturalOnly,
      notifyValueChange,
      rest,
    ],
  );

  const handleCompositionStart = useCallback(
    (e: React.CompositionEvent<HTMLInputElement>) => {
      e.preventDefault();
      if (rest.onCompositionStart) {
        rest.onCompositionStart(e);
      }
    },
    [rest],
  );

  const updateValue = useCallback(
    (newValue: string) => {
      if (disabled) return;

      const valueWithoutUnit = unit
        ? newValue.replace(new RegExp(`${unit}$`), '')
        : newValue;
      let filteredValue = filterInput(valueWithoutUnit);

      if (
        finalMaxLength !== undefined &&
        filteredValue.length > finalMaxLength
      ) {
        filteredValue = filteredValue.slice(0, finalMaxLength);
      }

      if (focused) {
        setInputValue(filteredValue);
      } else {
        if (unit && filteredValue) {
          setInputValue(`${filteredValue}${unit}`);
        } else {
          setInputValue(filteredValue);
        }
      }

      const numValue = Number(filteredValue);
      if (!Number.isNaN(numValue)) {
        let clampedValue = numValue;
        if (finalMin !== undefined && finalMin !== null) {
          clampedValue = Math.max(clampedValue, finalMin);
        }
        if (finalMax !== undefined && finalMax !== null) {
          clampedValue = Math.min(clampedValue, finalMax);
        }

        if (setValue) {
          setValue({ num: clampedValue, commitToUndoStack: true });
        } else if (onChange) {
          onChange(clampedValue);
        }
      } else if (filteredValue === '' || filteredValue === '-') {
        if (onChange) {
          onChange(filteredValue);
        }
      }
    },
    [
      disabled,
      filterInput,
      finalMaxLength,
      finalMin,
      finalMax,
      onChange,
      setValue,
      unit,
      focused,
    ],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateValue(e.target.value);
    },
    [updateValue],
  );

  const handleCompositionEnd = useCallback(
    (e: React.CompositionEvent<HTMLInputElement>) => {
      e.preventDefault();
      const input = e.currentTarget;
      const filtered = filterInput(input.value);

      if (filtered !== input.value) {
        input.value = filtered;
        updateValue(filtered);
      }

      if (rest.onCompositionEnd) {
        rest.onCompositionEnd(e);
      }
    },
    [filterInput, updateValue, rest],
  );

  const handleInput = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const input = e.currentTarget;
      const originalValue = input.value;
      let newValue = filterInput(originalValue);

      if (finalMaxLength !== undefined && newValue.length > finalMaxLength) {
        newValue = newValue.slice(0, finalMaxLength);
      }

      if (newValue !== originalValue) {
        input.value = newValue;
        updateValue(newValue);
      }
    },
    [filterInput, finalMaxLength, updateValue],
  );

  const handleFocus = useCallback(() => {
    setFocused(true);
    setWasMixedOnFocus(hasMixedValue);
    if (hasMixedValue) {
      setInputValue('');
    } else {
      const valueStr = String(value ?? defaultValue ?? '');
      if (unit && valueStr) {
        setInputValue(valueStr.replace(new RegExp(`${unit}$`), ''));
      } else {
        setInputValue(valueStr);
      }
    }
    inputRef.current?.select();
  }, [hasMixedValue, value, defaultValue, unit]);

  const handleBlur = useCallback(() => {
    setFocused(false);
    const mixedDuringFocus = wasMixedOnFocus;
    setWasMixedOnFocus(false);

    const trimmed = inputValue.trim();
    const fallbackCandidate =
      defaultValue !== undefined
        ? Number(defaultValue)
        : finalMin !== undefined && finalMin !== null
          ? finalMin
          : 0;
    const fallback = Math.min(
      Math.max(finalMin ?? -Infinity, fallbackCandidate),
      finalMax ?? Infinity,
    );

    if (trimmed === '') {
      if (mixedDuringFocus) {
        if (hasMixedValue) {
          setInputValue(mixedLabel);
          return;
        }
        const valueStr = String(value ?? '');
        if (unit && valueStr) {
          setInputValue(`${valueStr}${unit}`);
        } else {
          setInputValue(valueStr);
        }
        return;
      }

      const currentNum = value !== undefined ? Number(value) : NaN;
      if (!Number.isNaN(currentNum) && currentNum !== fallback) {
        if (setValue) {
          setValue({ num: fallback, commitToUndoStack: true });
        } else if (onChange) {
          onChange(fallback);
        }
      }
      const fallbackStr = String(fallback);
      if (unit) {
        setInputValue(`${fallbackStr}${unit}`);
      } else {
        setInputValue(fallbackStr);
      }
      return;
    }

    const valueWithoutUnit = unit
      ? trimmed.replace(new RegExp(`${unit}$`), '')
      : trimmed;
    const parsed = Number(valueWithoutUnit);
    if (Number.isNaN(parsed)) {
      const valueStr = String(value ?? '');
      if (unit) {
        setInputValue(`${valueStr}${unit}`);
      } else {
        setInputValue(valueStr);
      }
      return;
    }

    const clamped = Math.min(
      Math.max(finalMin ?? -Infinity, parsed),
      finalMax ?? Infinity,
    );

    if (clamped !== Number(value)) {
      if (setValue) {
        setValue({ num: clamped, commitToUndoStack: true });
      } else if (onChange) {
        onChange(clamped);
      }
    }
    const clampedStr = String(clamped);
    if (unit) {
      setInputValue(`${clampedStr}${unit}`);
    } else {
      setInputValue(clampedStr);
    }
  }, [
    inputValue,
    wasMixedOnFocus,
    defaultValue,
    finalMin,
    finalMax,
    hasMixedValue,
    mixedLabel,
    value,
    unit,
    setValue,
    onChange,
  ]);

  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;

      if (!naturalOnly) {
        setInputValue(rawValue);
        if (rawValue.trim() === '') return;

        const valueWithoutUnit = unit
          ? rawValue.replace(new RegExp(`${unit}$`), '')
          : rawValue;

        const newValue = Number(valueWithoutUnit);
        if (!Number.isNaN(newValue)) {
          const clamped = Math.min(
            Math.max(finalMin ?? -Infinity, newValue),
            finalMax ?? Infinity,
          );
          if (setValue) {
            setValue({ num: clamped, commitToUndoStack: true });
          } else if (onChange) {
            onChange(clamped);
          }
        }
        return;
      }

      const valueWithoutUnit = unit
        ? rawValue.replace(new RegExp(`${unit}$`), '')
        : rawValue;
      const filteredValue = filterInput(valueWithoutUnit);
      const displayValue =
        unit && filteredValue ? `${filteredValue}${unit}` : filteredValue;
      setInputValue(displayValue);
      if (filteredValue.trim() === '') return;

      const newValue = Number(filteredValue);
      if (!Number.isNaN(newValue)) {
        const clamped = Math.min(
          Math.max(finalMin ?? -Infinity, newValue),
          finalMax ?? Infinity,
        );
        if (setValue) {
          setValue({ num: clamped, commitToUndoStack: true });
        } else if (onChange) {
          onChange(clamped);
        }
      }
    },
    [setValue, onChange, unit, finalMin, finalMax, naturalOnly, filterInput],
  );

  const onLabelMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!enableDrag || disabled) return;

      e.preventDefault();
      e.stopPropagation();

      const currentValue =
        value !== undefined
          ? Number(value)
          : defaultValue !== undefined
            ? Number(defaultValue)
            : 0;
      setInitialValue(currentValue);
      setInitialX(e.clientX);
      setDragging(true);
      document.body.style.cursor = 'ew-resize';
    },
    [enableDrag, disabled, value, defaultValue],
  );

  const calculatePosition = useCallback(
    (e: MouseEvent) => {
      const deltaX = e.clientX - initialX;
      const sensitivity = 0.1;
      const rounded =
        initialValue + Math.round(deltaX * sensitivity) * finalStep;
      const roundedToStep = roundToStep(rounded, finalStep);
      return Math.min(
        Math.max(finalMin ?? -Infinity, roundedToStep),
        finalMax ?? Infinity,
      );
    },
    [initialValue, initialX, finalStep, finalMin, finalMax],
  );

  const handlePointerMove = useCallback(
    (e: MouseEvent) => {
      const newValue = calculatePosition(e);
      const newValueStr = String(newValue);
      setInputValue(
        unit && newValueStr ? `${newValueStr}${unit}` : newValueStr,
      );
      if (setValue) {
        setValue({ num: newValue, commitToUndoStack: false });
      } else if (onChange) {
        onChange(newValue);
      }
    },
    [calculatePosition, onChange, setValue, unit],
  );

  const handlePointerUp = useCallback(
    (e: MouseEvent) => {
      const newValue = calculatePosition(e);
      const newValueStr = String(newValue);
      setInputValue(
        unit && newValueStr ? `${newValueStr}${unit}` : newValueStr,
      );
      if (setValue) {
        setValue({ num: newValue, commitToUndoStack: true });
      } else if (onChange) {
        onChange(newValue);
      }
      document.body.style.cursor = '';
      setDragging(false);
    },
    [calculatePosition, onChange, setValue, unit],
  );

  useEffect(() => {
    if (!dragging) return;

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);

    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.body.style.cursor = '';
    };
  }, [dragging, handlePointerMove, handlePointerUp]);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pastedText = e.clipboardData.getData('text');
      const filtered = filterInput(pastedText);

      if (filtered && e.currentTarget) {
        let finalValue = filtered;
        if (finalMaxLength !== undefined) {
          finalValue = filtered.slice(0, finalMaxLength);
        }

        e.currentTarget.value = finalValue;
        setInputValue(finalValue);

        const syntheticEvent = {
          ...e,
          target: e.currentTarget,
          currentTarget: e.currentTarget,
        } as unknown as React.ChangeEvent<HTMLInputElement>;

        handleInputChange(syntheticEvent);
      }
    },
    [filterInput, finalMaxLength, handleInputChange],
  );

  const handleStepperClick = useCallback(
    (direction: 'up' | 'down') => {
      if (disabled) return;

      const newValue = getValueInputSteppedValue({
        value: getCurrentNumericValue(),
        direction,
        step: finalStep,
        min: finalMin,
        max: finalMax,
        naturalOnly,
      });
      notifyValueChange(newValue, true);
    },
    [
      disabled,
      finalStep,
      finalMin,
      finalMax,
      getCurrentNumericValue,
      naturalOnly,
      notifyValueChange,
    ],
  );

  const currentPlaceholder = focused
    ? String(value ?? defaultValue ?? '')
    : undefined;

  return (
    <div
      data-focused={focused}
      data-selected={isDropdownOpen}
      data-custom-state={disabled ? 'disabled' : 'default'}
      className={cn('group relative flex flex-col gap-5xs', className)}
    >
      {isShowLabel && label && (
        <div className={S.label.wrapper}>
          <label
            htmlFor={accessibilityLabel}
            className={cn(
              S.label.text,
              'group-data-[custom-state=disabled]:text-sys-cont-neutral-disabled-light',
              enableDrag && !disabled && 'cursor-ew-resize',
            )}
            onMouseDown={onLabelMouseDown}
          >
            {label}
          </label>
        </div>
      )}
      <div
        className={cn(
          'relative flex w-full items-center',
          hasStepper && 'gap-5xs',
        )}
      >
        <div
          className={cn(
            S.container,
            'flex-1 overflow-hidden',
            'hover:has-not-[input:disabled]:has-not-[input:focus]:bg-comp-value-input-bg-hover',
            'active:has-not-[input:disabled]:has-not-[input:focus]:bg-comp-value-input-bg-active',
            'group-data-[selected=true]:border group-data-[selected=true]:border-comp-value-input-border-selected group-data-[selected=true]:bg-comp-value-input-bg-selected',
            'has-[input:focus]:border has-[input:focus]:border-comp-value-input-border-focus has-[input:focus]:bg-comp-value-input-bg-focus',
            'has-[input:disabled]:bg-comp-value-input-bg-disabled',
            'transition-colors duration-200 ease-out',
          )}
        >
          {isShowLeadingNode && (
            <div
              className={cn(
                S.leadingNode.base,
                disabled && 'opacity-50',
                enableDrag && !disabled && 'cursor-ew-resize',
              )}
              onMouseDown={onLabelMouseDown}
            >
              {leadingNodeType === 'text' && leadingNodeValue && (
                <span
                  className={cn(
                    'text-comp-accent-medium text-sys-cont-neutral-default',
                    'group-data-[custom-state=disabled]:text-sys-cont-neutral-disabled-light',
                  )}
                >
                  {leadingNodeValue as string}
                </span>
              )}
              {leadingNodeType === 'icon' && (
                <div className="group-data-[custom-state=disabled]:opacity-50">
                  {leadingNodeValue ? (
                    <div
                      className={cn(
                        'size-[20px]',
                        S.leadingNode.icon,
                        'group-data-[custom-state=disabled]:[&_path]:fill-sys-cont-neutral-disabled-light',
                      )}
                    >
                      {leadingNodeValue as React.ReactNode}
                    </div>
                  ) : (
                    <PersonFill
                      className={cn(
                        'size-[20px]',
                        S.leadingNode.icon,
                        'group-data-[custom-state=disabled]:[&_path]:fill-sys-cont-neutral-disabled-light',
                      )}
                    />
                  )}
                </div>
              )}
            </div>
          )}
          <div className="flex min-w-0 flex-1 items-center gap-4xs">
            <input
              ref={inputRef}
              id={accessibilityLabel}
              type="text"
              inputMode={naturalOnly ? 'numeric' : 'decimal'}
              pattern={naturalOnly ? '[0-9]*' : '[0-9.\\-]*'}
              className={cn(
                'flex w-full transition-all outline-none',
                'text-comp-moderate-medium',
                'text-comp-value-input-text-default',
                'placeholder:text-sys-cont-neutral-disabled-default',
                'caret-comp-value-input-border-focus',
                'bg-transparent',
                'cursor-default',
                '[appearance:textfield]',
                '[&::-webkit-inner-spin-button]:appearance-none',
                '[&::-webkit-outer-spin-button]:appearance-none',
                'focus:placeholder:text-sys-cont-neutral-disabled-default',
                'disabled:text-comp-value-input-text-disabled',
                'disabled:placeholder:text-comp-value-input-text-disabled',
                'disabled:pointer-events-none disabled:cursor-not-allowed',
              )}
              placeholder={currentPlaceholder}
              value={inputValue}
              onBeforeInput={handleBeforeInput}
              onInput={handleInput}
              onChange={handleOnChange}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onCompositionStart={handleCompositionStart}
              onCompositionEnd={handleCompositionEnd}
              onPaste={handlePaste}
              maxLength={finalMaxLength}
              disabled={disabled}
              aria-label={accessibilityLabel}
              {...rest}
            />
          </div>
          {hasDropdown && (
            <button
              type="button"
              className={cn(
                'flex items-center justify-center',
                'cursor-pointer transition-all',
                'disabled:cursor-not-allowed',
                'flex-shrink-0 self-stretch px-2xs',
                disabled && 'opacity-50',
              )}
              onClick={onDropdownClick}
              disabled={disabled}
              aria-label="Open dropdown"
            >
              <ArrowDropDown
                className={cn(
                  S.chevron.base,
                  isDropdownOpen && S.chevron.open,
                  'group-data-[custom-state=disabled]:[&_path]:fill-sys-cont-neutral-disabled-light',
                )}
              />
            </button>
          )}
        </div>
        {hasStepper && (
          <Stepper
            onIncrement={() => handleStepperClick('up')}
            onDecrement={() => handleStepperClick('down')}
            disableIncrement={
              disabled ||
              (finalMax !== null &&
                finalMax !== undefined &&
                (value !== undefined ? Number(value) : 0) >= finalMax)
            }
            disableDecrement={
              disabled ||
              (finalMin !== null &&
                finalMin !== undefined &&
                (value !== undefined ? Number(value) : 0) <= finalMin)
            }
            disabled={disabled}
          />
        )}
      </div>
    </div>
  );
}
